// ─── Galeri foto halaman detail produk ───────────────────────────────────────
//
// Foto per-OPSI variasi (`variant_options.image_url`, diunggah admin lewat
// toggle "Gambar per opsi") sebelumnya hanya muncul sebagai thumbnail 20×20 di
// dalam tombol variasi. Padahal itu foto berukuran penuh — dikompres lewat
// jalur yang SAMA dengan foto produk (`compressImageFile` → bucket
// `product-media`) — jadi pembeli tak pernah bisa melihat warna yang ia pilih
// dalam ukuran besar.
//
// Modul ini menyatukan keduanya jadi SATU daftar slider: foto produk dulu
// (cover tetap slide 0, jadi pratinjau tautan & kesan pertama tak berubah),
// lalu foto opsi yang belum ada di daftar. Sekaligus memulangkan peta
// `optionId → indeks slide` supaya mengklik opsi bisa melompatkan slider ke
// foto opsi itu.
//
// Pemicunya DATA, bukan flag `useAsImagePrimary`: opsi yang PUNYA `imageUrl`
// ikut, titik. Sama persis dengan yang sudah dilakukan `VariantGroupSelector`
// saat merender thumbnail-nya — mematok flag akan membuat foto yang tampil di
// tombol justru hilang dari slider saat admin mematikan toggle-nya belakangan
// (image_url TIDAK ikut dihapus saat toggle dimatikan).

import type { VariantGroup } from "@/lib/products";

export type ProductGallery = {
  /** Foto produk + foto per-opsi variasi, sudah dedup dan urut tayang. */
  images: string[];
  /**
   * `optionId` → indeks slide di `images`. Opsi TANPA foto sengaja tak masuk
   * peta: pemanggil membedakan "tak ada foto" (jangan geser slider) dari
   * "foto di slide 0" — dan `0` itu falsy, jadi keduanya harus dibedakan lewat
   * `undefined`, bukan lewat kebenaran nilainya.
   */
  optionImageIndex: Record<string, number>;
};

/**
 * Kunci pembanding dua URL foto yang menunjuk objek storage yang SAMA.
 *
 * Query string (`?t=…` cache-buster) dan fragment dibuang; kalau URL-nya objek
 * bucket `product-media`, yang dibandingkan cuma jalur SESUDAH nama bucket —
 * foto produk disusun dari `storage_path` + base project AKTIF (mig 2300)
 * sedangkan `variant_options.image_url` menyimpan URL publik apa adanya, jadi
 * origin keduanya bisa berbeda untuk file yang sama.
 */
export function galleryImageKey(url: string): string {
  const clean = url.trim().split("#")[0].split("?")[0];
  const marker = "/product-media/";
  const at = clean.indexOf(marker);
  return at === -1 ? clean : clean.slice(at + marker.length);
}

/**
 * Susun daftar slide galeri + peta opsi→slide untuk satu produk.
 *
 * Opsi yang fotonya SAMA dengan foto produk (atau dengan opsi lain — grup
 * Warna & grup Bahan bisa memakai file yang sama) tidak menambah slide baru;
 * ia menunjuk slide yang sudah ada, supaya slider tidak berisi foto kembar.
 */
export function buildProductGallery(
  images: readonly string[] | null | undefined,
  variantGroups: readonly VariantGroup[] | null | undefined
): ProductGallery {
  const out: string[] = [];
  const seen = new Map<string, number>();
  const optionImageIndex: Record<string, number> = {};

  const push = (raw: string): number => {
    const url = raw.trim();
    const key = galleryImageKey(url);
    const existing = seen.get(key);
    if (existing !== undefined) return existing;
    const index = out.length;
    out.push(url);
    seen.set(key, index);
    return index;
  };

  for (const image of images ?? []) {
    if (typeof image === "string" && image.trim()) push(image);
  }

  for (const group of variantGroups ?? []) {
    for (const option of group.options ?? []) {
      const url = option.imageUrl;
      if (typeof url !== "string" || !url.trim()) continue;
      optionImageIndex[option.id] = push(url);
    }
  }

  return { images: out, optionImageIndex };
}
